import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { 
  Languages, Search, Loader2, UserPlus, UserMinus, Check, X, 
  User, ArrowLeft, Mail, Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE } from '../config/api';
const API = `${API_BASE}`;

const FriendsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user) {
      fetchFriends();
      fetchRequests();
    }
  }, [user]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery.length >= 3) {
        searchUsers();
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const fetchFriends = async () => {
    try {
      const response = await axios.get(`${API}/friends`, { withCredentials: true });
      setFriends(response.data);
    } catch (error) {
      toast.error('Failed to load friends');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/friends/requests`, { withCredentials: true });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to load requests');
    }
  };

  const searchUsers = async () => {
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(searchQuery)}`, { withCredentials: true });
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const sendFriendRequest = async (email) => {
    setActionLoading(email);
    try {
      await axios.post(`${API}/friends/request`, { to_user_email: email }, { withCredentials: true });
      toast.success('Friend request sent!');
      setSearchResults(searchResults.map(u => 
        u.email === email ? {...u, has_pending_request: true} : u
      ));
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setActionLoading(null);
    }
  };

  const acceptRequest = async (requestId, fromUserName) => {
    setActionLoading(requestId);
    try {
      await axios.post(`${API}/friends/requests/${requestId}/accept`, {}, { withCredentials: true });
      toast.success(`You are now friends with ${fromUserName}!`);
      fetchFriends();
      fetchRequests();
    } catch (error) {
      toast.error('Failed to accept request');
    } finally {
      setActionLoading(null);
    }
  };

  const declineRequest = async (requestId) => {
    setActionLoading(requestId);
    try {
      await axios.post(`${API}/friends/requests/${requestId}/decline`, {}, { withCredentials: true });
      toast.success('Request declined');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to decline request');
    } finally {
      setActionLoading(null);
    }
  };

  const removeFriend = async (friendId, friendName) => {
    if (!window.confirm(`Remove ${friendName} from your friends?`)) return;
    setActionLoading(friendId);
    try {
      await axios.delete(`${API}/friends/${friendId}`, { withCredentials: true });
      toast.success(`${friendName} removed from friends`);
      setFriends(friends.filter(f => f.user_id !== friendId));
    } catch (error) {
      toast.error('Failed to remove friend');
    } finally {
      setActionLoading(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  const pendingCount = requests.incoming.length;

  return (
    <div className="min-h-screen bg-background noise-overlay">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-foreground-muted hover:text-foreground p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-primary/20 flex items-center justify-center">
                <Languages className="w-5 h-5 text-primary" />
              </div>
              <span className="font-heading text-xl font-semibold text-foreground">
                <span className="text-primary/70">Language</span><span className="text-primary">Constructor</span>
              </span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-heading text-3xl font-bold text-foreground mb-6" data-testid="friends-title">
            Friends
          </h1>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-background-paper border border-border mb-6">
              <TabsTrigger value="friends" data-testid="tab-friends">
                <Users className="w-4 h-4 mr-2" />
                Friends ({friends.length})
              </TabsTrigger>
              <TabsTrigger value="requests" data-testid="tab-requests" className="relative">
                <Mail className="w-4 h-4 mr-2" />
                Requests
                {pendingCount > 0 && (
                  <span className="ml-2 bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="search" data-testid="tab-search">
                <Search className="w-4 h-4 mr-2" />
                Find Friends
              </TabsTrigger>
            </TabsList>

            {/* Friends Tab */}
            <TabsContent value="friends">
              {friends.length > 0 ? (
                <div className="space-y-3" data-testid="friends-list">
                  {friends.map(friend => (
                    <Card key={friend.user_id} className="bg-background-paper border-border">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            {friend.picture ? (
                              <img src={friend.picture} alt="" className="w-10 h-10 rounded-full" />
                            ) : (
                              <User className="w-5 h-5 text-primary" />
                            )}
                          </div>
                          <div>
                            <p className="text-foreground font-medium">{friend.name}</p>
                            <p className="text-foreground-muted text-sm">{friend.email}</p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeFriend(friend.user_id, friend.name)}
                          disabled={actionLoading === friend.user_id}
                          className="text-foreground-muted hover:text-destructive"
                          data-testid={`remove-friend-${friend.user_id}`}
                        >
                          {actionLoading === friend.user_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <UserMinus className="w-4 h-4" />
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-background-paper border border-border flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-foreground-subtle" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-foreground mb-2">No friends yet</h2>
                  <p className="text-foreground-muted mb-4">Search for users to add them as friends.</p>
                  <Button onClick={() => setActiveTab('search')} variant="outline" className="border-border">
                    <Search className="w-4 h-4 mr-2" />
                    Find Friends
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Requests Tab */}
            <TabsContent value="requests">
              {requests.incoming.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-foreground font-medium mb-3">Incoming Requests</h3>
                  <div className="space-y-3" data-testid="incoming-requests">
                    {requests.incoming.map(req => (
                      <Card key={req.request_id} className="bg-background-paper border-border">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="text-foreground font-medium">{req.from_user_name}</p>
                              <p className="text-foreground-muted text-sm">{req.from_user_email}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm"
                              onClick={() => acceptRequest(req.request_id, req.from_user_name)}
                              disabled={actionLoading === req.request_id}
                              className="bg-primary hover:bg-primary/90"
                              data-testid={`accept-${req.request_id}`}
                            >
                              {actionLoading === req.request_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => declineRequest(req.request_id)}
                              disabled={actionLoading === req.request_id}
                              className="border-border"
                              data-testid={`decline-${req.request_id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {requests.outgoing.length > 0 && (
                <div>
                  <h3 className="text-foreground font-medium mb-3">Sent Requests (Pending)</h3>
                  <div className="space-y-3" data-testid="outgoing-requests">
                    {requests.outgoing.map(req => (
                      <Card key={req.request_id} className="bg-background-paper border-border opacity-75">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="text-foreground font-medium">{req.to_user_name}</p>
                              <p className="text-foreground-muted text-sm">{req.to_user_email}</p>
                            </div>
                          </div>
                          <span className="text-foreground-subtle text-sm">Pending</span>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {requests.incoming.length === 0 && requests.outgoing.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-background-paper border border-border flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-foreground-subtle" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-foreground mb-2">No pending requests</h2>
                  <p className="text-foreground-muted">Friend requests you send or receive will appear here.</p>
                </div>
              )}
            </TabsContent>

            {/* Search Tab */}
            <TabsContent value="search">
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-subtle" />
                <Input
                  placeholder="Search by email or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-background-paper border-border"
                  data-testid="user-search-input"
                />
              </div>

              {searchLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-3" data-testid="search-results">
                  {searchResults.map(u => (
                    <Card key={u.user_id} className="bg-background-paper border-border">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            {u.picture ? (
                              <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                            ) : (
                              <User className="w-5 h-5 text-primary" />
                            )}
                          </div>
                          <div>
                            <p className="text-foreground font-medium">{u.name}</p>
                            <p className="text-foreground-muted text-sm">{u.email}</p>
                          </div>
                        </div>
                        {u.is_friend ? (
                          <span className="text-secondary text-sm flex items-center gap-1">
                            <Check className="w-4 h-4" /> Friends
                          </span>
                        ) : u.has_pending_request ? (
                          <span className="text-foreground-muted text-sm">Request Pending</span>
                        ) : (
                          <Button 
                            size="sm"
                            onClick={() => sendFriendRequest(u.email)}
                            disabled={actionLoading === u.email}
                            className="bg-primary hover:bg-primary/90"
                            data-testid={`add-friend-${u.user_id}`}
                          >
                            {actionLoading === u.email ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <UserPlus className="w-4 h-4 mr-2" />
                                Add Friend
                              </>
                            )}
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : searchQuery.length >= 3 ? (
                <div className="text-center py-8">
                  <p className="text-foreground-muted">No users found matching "{searchQuery}"</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-foreground-muted">Enter at least 3 characters to search</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default FriendsPage;
